import React from 'react';
import { FolderAddOutlined, SearchOutlined } from '@ant-design/icons';
import LayoutGrid from 'lucide-react/dist/esm/icons/layout-grid.mjs';
import SlidersHorizontal from 'lucide-react/dist/esm/icons/sliders-horizontal.mjs';
import { FlareLogoMark, SessionListPane } from '../../../index.js';
import SidebarInstanceStamp from './SidebarInstanceStamp.jsx';

function resolveLogoImageSlot(logoUrl, logoAlt) {
  const resolvedLogoUrl = String(logoUrl || '').trim();
  if (!resolvedLogoUrl) {
    return null;
  }
  return (
    <img
      src={resolvedLogoUrl}
      alt={String(logoAlt || 'logo')}
      style={{
        display: 'block',
        height: 28,
        maxWidth: '100%',
        objectFit: 'contain',
        width: 'auto',
      }}
    />
  );
}

function resolveSidebarLogoSlot(resolvedUILabels = {}, themeTokens) {
  return resolveLogoImageSlot(
    resolvedUILabels.sidebar_logo_url,
    resolvedUILabels.sidebar_logo_alt,
  ) ?? (
    <FlareLogoMark
      label={resolvedUILabels.sidebar_logo_label}
      tag={resolvedUILabels.sidebar_logo_tag}
      themeTokens={themeTokens}
    />
  );
}

function resolveCollapsedLogoSlot(resolvedUILabels = {}) {
  return resolveLogoImageSlot(
    resolvedUILabels.product_logo_url || resolvedUILabels.empty_state_logo_url || resolvedUILabels.sidebar_logo_url,
    resolvedUILabels.product_logo_alt || resolvedUILabels.empty_state_logo_alt || resolvedUILabels.sidebar_logo_alt,
  );
}

function resolveSidebarStamp({ collapsed, mobileSidebar, sidebarStamp, productName }) {
  if (collapsed || mobileSidebar) {
    return null;
  }
  return <SidebarInstanceStamp {...sidebarStamp} productName={productName} />;
}

export default function SessionPane({
  themeTokens,
  managementAvailable = false,
  viewModel,
  actionGuards,
  apiReadinessGate,
  projectItems,
  projectSlot,
  sessions,
  resolvedUILabels,
  setActiveWorkspaceTab,
  sidebarHeaderActions,
  sidebarStamp,
  productName,
  collapsed,
  mobileSidebar = false,
  showHeader = true,
  onAfterSidebarAction,
}) {
  const logoSlot = collapsed
    ? resolveCollapsedLogoSlot(resolvedUILabels)
    : resolveSidebarLogoSlot(resolvedUILabels, themeTokens);
  const sidebarStampNode = resolveSidebarStamp({ collapsed, mobileSidebar, sidebarStamp, productName });
  const handleAfterAction = () => {
    if (typeof onAfterSidebarAction === 'function') {
      onAfterSidebarAction();
    }
  };
  const sidebarNavigation = viewModel.sessionList.sidebarNavigation;
  const projectOperations = [
    { icon: <SearchOutlined />, key: 'project_search', label: '搜索' },
    { icon: <FolderAddOutlined />, key: 'project_create', label: '新建项目' },
    {
      icon: <LayoutGrid aria-hidden="true" size={16} strokeWidth={2} />,
      key: 'project_management',
      label: resolvedUILabels.project_management || '项目空间',
    },
    { icon: <SlidersHorizontal aria-hidden="true" size={16} strokeWidth={2} />, key: 'skill_management', label: '配置' },
  ].filter(operation => operation.key !== 'skill_management' || managementAvailable);
  const operationHandlers = new Map([
    ['project_search', () => setActiveWorkspaceTab?.('projects')],
    ['project_create', () => {
      setActiveWorkspaceTab?.('chats');
      return actionGuards.handleCreateProject?.();
    }],
    ['project_management', () => setActiveWorkspaceTab?.('projects')],
    ['skill_management', () => setActiveWorkspaceTab?.('skills')],
  ]);

  return (
    <div style={{ height: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
      <SessionListPane
        sidebarNavigation={sidebarNavigation}
        activeSessionId={viewModel.sessionList.activeSessionId}
        activeSessionRunning={viewModel.timeline.loading}
        loadingSessionId={viewModel.sessionList.loadingSessionId}
        runningSessionIds={viewModel.sessionList.runningSessionIds}
        error={actionGuards.sessionPaneError}
        recovery={viewModel.sessionList.recovery}
        onRetryLoad={viewModel.sessionList.onRetryLoad}
        loading={Boolean(viewModel.sessionList.loading)}
        loadingMore={viewModel.sessionList.loadingMore}
        hasMore={viewModel.sessionList.hasMore}
        createProjectDisabled={actionGuards.createProjectPending}
        onCreateProject={(options) => {
          setActiveWorkspaceTab?.('chats');
          const result = actionGuards.handleCreateProject?.(options);
          handleAfterAction();
          return result;
        }}
        onCreateSession={async (project) => {
          if (!await sidebarNavigation?.createSession(project)) return false;
          setActiveWorkspaceTab?.('chats');
          handleAfterAction();
          return true;
        }}
        onOperationClick={(operation) => {
          operationHandlers.get(operation?.key)?.();
          handleAfterAction();
        }}
        onProjectSelect={(project) => {
          sidebarNavigation?.openProject(project);
          setActiveWorkspaceTab?.('chats');
        }}
        onSelectSession={async (sessionId, projectId) => {
          if (!await sidebarNavigation?.openSession(projectId, sessionId)) return false;
          setActiveWorkspaceTab?.('chats');
          handleAfterAction();
          return true;
        }}
        onArchiveProject={viewModel.sessionList.onArchiveProject}
        onPinProject={viewModel.sessionList.onPinProject}
        onLoadMore={viewModel.sessionList.onLoadMore}
        projectHasMore={viewModel.sessionList.projectHasMore}
        projectContinuationKey={viewModel.sessionList.projectContinuationKey}
        collectionScopeKey={viewModel.sessionList.collectionScopeKey}
        projectLoading={viewModel.sessionList.projectLoading}
        projectLoadingMore={viewModel.sessionList.projectLoadingMore}
        onLoadMoreProjects={viewModel.sessionList.onLoadMoreProjects}
        onSearchProjects={viewModel.sessionList.onSearchProjects}
        onSearchSessions={viewModel.sessionList.onSearch}
        onRenameSession={viewModel.sessionList.onRenameSession}
        onArchiveSession={viewModel.sessionList.onArchiveSession}
        onPinSession={viewModel.sessionList.onPinSession}
        operations={projectOperations}
        operationsTitle={resolvedUILabels.sidebar_operations_title}
        projectItems={projectItems}
        projectSlot={projectSlot}
        compact={false}
        collapsed={collapsed}
        mobileSidebar={mobileSidebar}
        surfaceBackground={mobileSidebar ? themeTokens.sidebarBg : 'transparent'}
        showHeader={showHeader}
        emptyProjectsLabel={resolvedUILabels.project_list_empty}
        emptySessionsLabel="暂无会话"
        loadErrorMessage={apiReadinessGate.error ? 'API 预检失败' : '加载会话列表失败'}
        createProjectLabel={resolvedUILabels.new_project_button}
        createSessionInProjectLabel={resolvedUILabels.new_session_button}
        footerSlot={sidebarStampNode}
        logoSlot={logoSlot}
        sidebarHeaderActions={sidebarHeaderActions}
        sessions={sessions}
        themeTokens={themeTokens}
      />
    </div>
  );
}
